import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, Grid, Icon, IconButton, LinearProgress, TextField, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { makeStyles } from '@mui/styles';
import { DataGrid, GridColumns, GridValueGetterParams } from "@mui/x-data-grid";
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useRecoilValue } from "recoil";
import { SalaryService } from "../../../Component/Api/EmploymentEquity/Salary/SalaryService";
import { IndividualService } from '../../../Component/Api/Individual/IndividualService';
import AppAccordion from '../../../Component/Common/Components/Accordion/AppAccordion';
import AppCheckbox from '../../../Component/Common/Components/FormValidationInputs/AppCheckbox';
import { AppDatepicker } from '../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import AppSelectList from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { MDButton } from "../../../Component/Common/Components/MDButton";
import { genderList, provinceList, raceList } from '../../../Component/Common/Constants/Lists/DropdownListOptions';
import { EE123PeopleRole_Edit, EE123SalariesRole_Edit, PeopleEditRole, UserAdminRole } from "../../../Component/Common/Constants/UserRoles";
import { SkeletonLoading } from '../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { Individual } from '../../../Component/Model/Dto/Individual';
import { SalaryDtos } from "../../../Component/Model/Dto/Salaries";
import { DateFormatter, defaultObject } from "../../../Component/Model/helpers";
import { decimalFormatter } from "../../../Helpers/DecimalFormatter";
import { userRolesState } from "../../../State/user.Atom";
import { CustomDialogTitle } from '../Custom/CustomDialogTitle';
import { individualSchema } from './IndividualValidation';
import { ConfirmationDialog } from '../../../Component/Common/Components/ConfirmationDialog';
import { UserActivityService } from "../../../Component/Api/UserActivity/UserActivityService";
import { CDMSummariesSalariesSection, EmployeeSalariesSection } from "../../../Component/Common/Constants/UserActivityTrackingSections";
import { UserActivityActionType } from "../../../Component/Model/Enum/BEE123Enums";

const useStyles = makeStyles({
  root: {
    "& .row--0": {
      backgroundColor: "#bdd7ee" //actual
    },
    "& .row--1": {
      backgroundColor: "white" //input
    },
    "& .row--2": {
      backgroundColor: "#e7e6e6" //percentage
    },
    "& .row--3": {
      backgroundColor: "#d6dce4" //headcountValue
    }
  }
});

interface IProps {
  id: number;
  scorecardId: string;
  showIndividualModal: boolean;
  closeIndividualModal: () => void;
  section?: string;
}

const availableOccupationLevelTypes: number[] = [2, 1, 4, 5, 6, 7, 8, 9, 10];


export const IndividualSalaryModal = (props: IProps) => {
  const classes = useStyles();
  const { control, reset, handleSubmit, formState: { isSubmitting }, setValue } = useForm({
    mode: 'all',
    resolver: yupResolver(individualSchema)
  });
  const [individualId, setIndividualId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addSalariesDialogOpen, setAddSalariesDialogOpen] = useState<boolean>(false);
  const [yearToAdd, setYearToAdd] = useState<number>();
  const [salaryData, setSalaryData] = useState<SalaryDtos.SalariesPerYearResponse[]>([]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);

  const editRoleAccess = [
    PeopleEditRole,
    EE123PeopleRole_Edit
  ];

  const salaryEditRoleAccess = [
    EE123SalariesRole_Edit,
    UserAdminRole
  ];

  let yearToDelete = 0;

  const userRoles = useRecoilValue(userRolesState);
  const isEditable = userRoles.some(r => editRoleAccess.includes(r));
  const canEditSalary = userRoles.some(r => salaryEditRoleAccess.includes(r));

  const salariesLog = (actionType: number, actionDescription: string) => {
    props.section && UserActivityService.createActivityLog({
      section: props.section,
      actionType: actionType,
      actionDescription: actionDescription,
      scorecardId: props.section === CDMSummariesSalariesSection ? undefined : props.scorecardId,
      requestUrl: window.location.href
    });
  }

  const getEmployeeData = async (id: number) => {
    setIndividualId(id);
    if (id === undefined || id === 0) return;
    try {
      const request: Individual.GetRequest = {
        id: id,
        scorecardId: props.scorecardId,
        LinkedType: undefined,
        InterventionId: undefined,
        filterVAStartDate: undefined,
        filterVAEndDate: undefined
      }
      setIsLoading(true);
      const result = await IndividualService.GetIndividualByID(request);
      props.section === EmployeeSalariesSection && salariesLog(UserActivityActionType.View, "View an employee's salary details");

      reset(result.data as Individual.IndividualDto);

      await GetSalaries(id);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  const GetSalaries = async (id: number) => {
    try {

      setIsLoading(true);
      const result = await SalaryService.GetIndividualSalariesForYears(id);
      setSalaryData(result.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  const hasSalaryData = (): boolean => {
    return salaryData && salaryData.length > 0;
  }

  const handleCloseConfirmationModal = () => {
    setShowConfirmationDialog(false);
  }

  const handleOpenConfirmationModal = () => {
    setShowConfirmationDialog(true);
  }

  const handleDataSubmit = async (data: FieldValues) => {
    try {
      const finalData = data as Individual.IndividualUpdateDto;
      finalData.SampledIndividualViewModel = null;
      const result = await IndividualService.SaveIndividual(finalData);
      salariesLog(UserActivityActionType.Update, "Update an individual's details");
      toast.success(`Employee updated successfully`);
      props.closeIndividualModal();
    } catch (error) {
      console.log(error)
    }
  }
  useEffect(() => {
    getEmployeeData(props.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showIndividualModal, props.id])

  const handleDeleteYear = async () => {
    try {
      setIsLoading(true);
      await SalaryService.DeleteIndividualSalariesForYear(individualId, yearToDelete)
        .then(async (result) => {
          if (result.status >= 200 && result.status <= 299) {
            await GetSalaries(individualId);
            salariesLog(UserActivityActionType.Delete, "Delete salary year");
            toast.success(`Salaries for year ${yearToDelete} successfully deleted`);
          }
        });
        setShowConfirmationDialog(false);
    } catch (error: any) {
      toast.error(error.response.data);
    } finally {
      setIsLoading(false);
    }
  }

  const processSalaryUpdate = async (newRow: SalaryDtos.EESalaryResponse, oldRow: SalaryDtos.EESalaryResponse) => {
    if (_.isEqual(newRow, oldRow))
      return newRow;

    try {
      let data = salaryData;
      let year = new Date(newRow.salaryDate).getFullYear();
      let idxBlock = data.findIndex(x => x.year === year);
      let block = data[idxBlock];
      if (!block)
        return oldRow;

      let idxSalary = _.findIndex(block.salaries, x => x.salaryDate === newRow.salaryDate);
      let salaries = block.salaries
      let salary = { ...salaries[idxSalary],
          variable: newRow.variable ?? 0,
          fixed: newRow.fixed ?? 0 } || defaultObject;
      salaries = salaries.filter(x => x.salaryDate !== newRow.salaryDate);
      salaries.push(salary);
      salaries = _.sortBy(salaries, x => new Date(x.salaryDate));
      block.salaries = salaries;
      data.splice(idxBlock, 1, block);
      setSalaryData(data);

      return newRow;

    } catch (error: any) {
      toast.error(error.response.data);
      return oldRow;
    } finally { }
  }

  const addYear = async () => {
    try {
      setIsLoading(true);

      if (!yearToAdd || isNaN(yearToAdd)) {
        toast.warning(`Please enter a valid year`);
        return;
      }

      if (salaryData.findIndex(x => x.year === yearToAdd) > -1) {
        toast.warning(`Year ${yearToAdd} rows already exists`);
        return;
      }

      await SalaryService.AddIndividualSalariesForYear(individualId, yearToAdd!)
        .then(async (result) => {
          setSalaryData([...salaryData, result.data]);
        });
      salariesLog(UserActivityActionType.Create, "Add salary year");
    } catch (error: any) {
      toast.error(error.response.data);
    } finally {
      setIsLoading(false);
      setAddSalariesDialogOpen(false);
      setYearToAdd(undefined);
    }
  }

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length === 4) {
      const value = parseInt(event.target.value, 10);
      setYearToAdd(isNaN(value) ? undefined : value);
    } else {
      setYearToAdd(undefined);
    }
  };

  //A data grid cell will only commit its content when the cell loses focus or when the enter or tab key is pressed
  //We simulate an enter key press to force the cell to commit its changes without the user having to manually perform this action before hitting save to save the year's salaries
  const simulateEnterKeyPress = () => {
    return new Promise<void>((resolve) => {  
      
      const enterKeyPressEvent = new KeyboardEvent('keydown', { key: 'Enter' });
      document.dispatchEvent(enterKeyPressEvent);
  
      // Added a delay to give time for the event to be processed
      setTimeout(() => {
        resolve();
      }, 100);
    });
  };

  const saveSalariesForYear = async (year: number) => {
      try{
        await simulateEnterKeyPress();
        
        const data: SalaryDtos.SalariesPerYearResponse | undefined = _.find(salaryData, x => x.year === year);
        if (!data)
          return;
        
        setIsLoading(true);

        await SalaryService.SaveIndividualSalariesForYear(individualId, data);
        const logText = props.section === CDMSummariesSalariesSection ? "Save salary details for a person" : "Update salary details for a year";
        salariesLog(UserActivityActionType.Update, logText);
        toast.success('Salaries for year succesfully saved');
      } catch (error: any) {
      toast.error(error.response.data);
      } finally {
        setIsLoading(false);
      }
  }

  const renderSalaries = () => {
    return hasSalaryData() ? (salaryData.sort((a, b) => b.year - a.year).map((blockItem: SalaryDtos.SalariesPerYearResponse, index) => {
      return (
        <AppAccordion
          expandOnDefault={true}
          label={blockItem.year.toString()}
          summaryActions={renderDeleteYear(blockItem.year)}
          key={`acc-year-${blockItem.year}`}>
          {
            renderSalariesForYear(blockItem.year, blockItem.salaries)
          }
        </AppAccordion>
      )
    })) : (<></>);
  }


  const colMonthWidth: number = 200;
  const colSalariesWidth: number = 150;
  const columns: GridColumns = [
    {
      field: 'salaryDate', headerName: 'Month', editable: false, width: colMonthWidth, type: 'string', sortable: false,
      valueGetter: (params: GridValueGetterParams) => {
        return DateFormatter((params.row as SalaryDtos.EESalaryResponse).salaryDate.toString(), 'MMMM');
      }
    },
    {
      field: 'ctc', headerName: 'CTC', editable: false, width: colSalariesWidth, type: 'string', sortable: false,
      valueFormatter: ({ value }) => "N/A"
    },
    {
      field: 'fixed', headerName: 'FIXED', editable: true, width: colSalariesWidth, type: 'number', sortable: false, 
      valueFormatter: ({ value  }) => decimalFormatter(value)
    },
    {
      field: 'variable', headerName: 'VARIABLE', editable: true, width: colSalariesWidth, type: 'number', sortable: false,
      valueFormatter: ({ value }) => decimalFormatter(value)
    },

  ];
  const aggregateColumns: GridColumns = [
    {
      field: 'salaryDate', headerName: 'Month', editable: false, width: colMonthWidth, type: 'string', sortable: false,
    },
    {
      field: 'ctc', headerName: 'CTC', editable: false, width: colSalariesWidth, type: 'string', sortable: false,
      valueFormatter: ({ value }) => "N/A"
    },
    {
      field: 'fixed', headerName: 'FIXED', editable: false, width: colSalariesWidth, type: 'number', sortable: false,
      valueFormatter: ({ value }) => decimalFormatter(value)
    },
    {
      field: 'variable', headerName: 'VARIABLE', editable: false, width: colSalariesWidth, type: 'number', sortable: false,
      valueFormatter: ({ value }) => decimalFormatter(value)
    },

  ];

  const annualizedColumns: GridColumns = [
    {
      field: 'salaryDate', headerName: 'Month', editable: false, width: colMonthWidth, type: 'string', sortable: false,
    },
    {
      field: 'ctc', headerName: 'CTC', editable: false, width: colSalariesWidth, type: 'string', sortable: false,
      valueFormatter: ({ value }) => "N/A"
    },
    {
      field: 'fixed', headerName: 'FIXED', editable: false, width: colSalariesWidth, type: 'number', sortable: false,
      valueFormatter: ({ value }) => decimalFormatter(value)
    },
    {
      field: 'variable', headerName: 'VARIABLE', editable: false, width: colSalariesWidth, type: 'number', sortable: false,
    },

  ];

  const renderSalariesForYear = (year: number, salaries: SalaryDtos.EESalaryResponse[]) => {
    let totalsFixed = salaries.reduce((sum, current) => sum + current.fixed, 0);
    let totalsVariable = salaries.reduce((sum, current) => sum + current.variable, 0);
    let summedFixedValue = 0;
    let fixedCount = 0;
    salaries.forEach(function (salary) {
      summedFixedValue += salary.fixed;
      fixedCount += salary.fixed > 0 ? 1 : 0;
    });

    const annualizedFixed = fixedCount > 0 ? (summedFixedValue / fixedCount) * 12 : 0;

    return salaries && salaries.length > 0 ?
      (<div>
        <Grid container mb={1}>
          <Grid item xs={12}>
            <DataGrid
              key={`salaries-grd-${year}`}
              headerHeight={70}
              className={classes.root}
              disableSelectionOnClick
              autoHeight
              hideFooter
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              rowHeight={25}
              rows={salaries}
              columns={columns}
              editMode="row"
              isCellEditable={(params) => (canEditSalary)}
              experimentalFeatures={{ newEditingApi: true }}
              processRowUpdate={processSalaryUpdate}
              components={{
                LoadingOverlay: LinearProgress,
              }}
              loading={isLoading}
            />
            <DataGrid
              key={`salaries-totals-grd-${year}`}
              headerHeight={0}
              className={classes.root}
              disableSelectionOnClick
              autoHeight
              hideFooter
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              rowHeight={30}
              rows={[
                { id: 1, salaryDate: 'TOTAL', fixed: totalsFixed, variable: totalsVariable }
              ]}
              columns={aggregateColumns}
              isCellEditable={(params) => (true)}
              components={{
                LoadingOverlay: LinearProgress,
              }}
              loading={isLoading}
            />
            <DataGrid
              key={`salaries-totals-grd-${year}`}
              headerHeight={0}
              className={classes.root}
              disableSelectionOnClick
              autoHeight
              hideFooter
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              rowHeight={30}
              rows={[
                { id: 1, salaryDate: 'Annualized', fixed: annualizedFixed, variable: '' }
              ]}
              columns={annualizedColumns}
              isCellEditable={(params) => (true)}
              components={{
                LoadingOverlay: LinearProgress,
              }}
              loading={isLoading}
            />
          </Grid>
        </Grid>
        {canEditSalary &&
          <MDButton color="primary" onClick={() => {
            saveSalariesForYear(year);
          }}> {'Save ' + year + " Salaries"}
          </MDButton>
        }
      </div>)
      :
      (<div>no salary rows for year</div>);
  }

  const renderDeleteYear = (year: number) => {
    yearToDelete = year
    return canEditSalary ? (
      <Tooltip title="Delete salary data">
        <IconButton
          aria-label="close" size="small"
          style={{ float: 'right', color: 'inherit' }}
          onClick={handleOpenConfirmationModal}>
          <Icon sx={{ color: '#fff' }}>delete</Icon>
        </IconButton>
      </Tooltip>) : undefined
  }

  return (
    <Dialog disableEnforceFocus open={props.showIndividualModal} onClose={() => { props.closeIndividualModal() }} aria-labelledby="form-dialog-title" maxWidth='lg'>
      <CustomDialogTitle id="individual-dialog-title" onClose={() => props.closeIndividualModal()}>
        {'View/Edit Employee & Salary Details'}
      </CustomDialogTitle>
      <DialogContent>
        {isLoading && <SkeletonLoading formLoading FieldAmount={6} width={350} />}
        {!isLoading &&
          <Box>
            <AppAccordion expandOnDefault={true} label={'Employee Data'}>
              <form id='IndividualForm' onSubmit={handleSubmit(handleDataSubmit)}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={12} mt={1}>
                    <AppTextInput control={control} name="identificationNumber" label="ID Number" disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name="employeeCode" label="Employee Code" disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name="firstName" label="Name" disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name="lastName" label="Surname" disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppSelectList control={control} name="gender" label="Gender" items={genderList} disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppSelectList control={control} name="raceId" label="Race" items={raceList} disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppSelectList control={control} name="province" label="Province" items={provinceList} disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppDatepicker control={control} name="dateOfBirth" label="Date Of Birth" inputFormat='yyyy/MM/dd' disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppCheckbox control={control} name='disabled' label='Disabled' disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppCheckbox control={control} name='isYes' label='Y.E.S. Employee' disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppCheckbox control={control} name='isYesAbsorbed' label='Y.E.S. Absorbed' disabled={!isEditable} />
                  </Grid>
                </Grid>
              </form>
            </AppAccordion>
            <Box mt={1} mb={1}>
              <Typography variant='h6' component='div' mb={1}>Salaries per year</Typography>
              {canEditSalary &&
                <MDButton variant="contained" color="secondary" onClick={() => {
                  setAddSalariesDialogOpen(true);
                }}>
                  Add Year
                </MDButton>
              }
            </Box>
            <Box>
              <Dialog open={addSalariesDialogOpen} maxWidth='sm'>
                <CustomDialogTitle id="customized-dialog-title" onClose={() => setAddSalariesDialogOpen(false)}>
                  Add salaries for year
                </CustomDialogTitle>
                <DialogContent >
                  <Box mt={1}>
                    <TextField
                      id="txt-yearToAdd-withValue"
                      label="Year"
                      type="number"
                      sx={{ width: '300px' }}
                      onChange={handleYearChange}
                      value={yearToAdd}
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Box>
                    <Button onClick={() => {
                      addYear();
                    }}>Add</Button>
                  </Box>
                </DialogActions>
              </Dialog>
              {renderSalaries()}
            </Box>
          </Box >}
      </DialogContent>
      <DialogActions>
        {isEditable &&
          <LoadingButton loading={isSubmitting} variant='contained' form='IndividualForm' type="submit" color='primary'>
            Save
          </LoadingButton>
        }
      </DialogActions>

      <ConfirmationDialog
                    showDialog={showConfirmationDialog}
                    handleAccept={handleDeleteYear}
                    handleCancel={handleCloseConfirmationModal}
                    heading={"Delete Salary Year"}
                    message={"Are you sure you want to delete this year?"}
                />

    </Dialog>
  )
}
